/**
 * HTTP Relay Client
 *
 * Sends requests through the relay server instead of directly to Kalshi.
 * This solves CORS and provides a consistent transport layer.
 */

import type { HttpRelayRequest, HttpRelayResponse } from '@galactus/shared';

const envRelayUrl = (import.meta.env.VITE_RELAY_HTTP_URL as string | undefined)?.trim();

function getRelayHttpUrl(): string {
  if (envRelayUrl && envRelayUrl.length > 0) {
    return envRelayUrl;
  }
  return import.meta.env.DEV ? 'http://localhost:8787/relay/http' : '/relay/http';
}

function getMtRelayHttpUrl(relayUrl: string): string {
  if (relayUrl.endsWith('/relay/http')) {
    return relayUrl.replace(/\/relay\/http$/, '/relay/mt-http');
  }
  if (relayUrl.startsWith('http')) {
    return `${new URL(relayUrl).origin}/relay/mt-http`;
  }
  return '/relay/mt-http';
}

const RELAY_URL = getRelayHttpUrl();
const MT_RELAY_URL = getMtRelayHttpUrl(RELAY_URL);

async function postRelay(
  url: string,
  envelope: HttpRelayRequest,
  accessToken?: string
): Promise<HttpRelayResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(envelope),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = await response.text().catch(() => 'Unknown error');
      throw new Error(`Relay error: ${response.status} ${error}`);
    }

    const result = (await response.json()) as HttpRelayResponse;
    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Relay error: Connection timeout. Is the relay server running on port 8787?');
    }
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error(
        "Relay error: Cannot connect to relay server. Make sure it's running on localhost:8787"
      );
    }
    throw error;
  }
}

/**
 * Forward HTTP request through relay
 */
export async function relayHttp(envelope: HttpRelayRequest): Promise<HttpRelayResponse> {
  return postRelay(RELAY_URL, envelope);
}

/**
 * Forward HTTP request through the MT relay path using JWT auth.
 */
export async function relayHttpMT(
  envelope: HttpRelayRequest,
  accessToken: string
): Promise<HttpRelayResponse> {
  return postRelay(MT_RELAY_URL, envelope, accessToken);
}

/**
 * Create a relay request envelope
 */
export function createRelayRequest(
  id: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  headers: Record<string, string>,
  body?: string
): HttpRelayRequest {
  return {
    id,
    method,
    url,
    headers,
    body,
  };
}
